import type { VisibilityState } from '@tanstack/react-table';

const DEFAULT_COLUMN_MIN_WIDTH = 120;

type ColumnFit = {
  /** Columns to drop, first to last, until the rest fit. */
  hideOrder: readonly string[];
  /** Minimum rendered width per column id, in pixels. */
  minWidths: Readonly<Record<string, number>>;
  /** Horizontal padding added to every visible column, in pixels. */
  gutter: number;
};

/**
 * Return which of `columnIds` stay visible in a table `width` pixels wide.
 * Columns outside `hideOrder` are always shown; a non-positive width shows everything.
 */
export function columnVisibilityForWidth(
  width: number,
  columnIds: string[],
  { hideOrder, minWidths, gutter }: ColumnFit
): VisibilityState {
  const visibility: VisibilityState = {};
  for (const id of columnIds) visibility[id] = true;
  if (width <= 0) return visibility;

  const needed = () =>
    columnIds
      .filter((id) => visibility[id] !== false)
      .reduce((sum, id) => sum + (minWidths[id] ?? DEFAULT_COLUMN_MIN_WIDTH) + gutter, 0);

  for (const id of hideOrder) {
    if (!columnIds.includes(id)) continue;
    if (needed() <= width) break;
    visibility[id] = false;
  }
  return visibility;
}
